import { useEffect, useRef, useState } from "react";
import { Navigation, CalendarX } from "lucide-react";
import { EventCard, type Event, type SeriesInfo } from "../calendar/EventCard";
import { EventModal } from "../calendar/EventModal";
import { ZipField, DistanceSelect } from "../filters/ZipCodeInput";
import { UnplaceableNote } from "./UnplaceableNote";
import { bucketByTime } from "@/lib/nearby";
import { ANY_DISTANCE } from "@/lib/distance";
import { getTodayISO } from "@/lib/dateUtils";
import type { GeocodingResult } from "@/lib/geocoding";

interface NearbyViewProps {
  /**
   * Events already filtered and measured — one entry per series, each carrying
   * `distanceMiles`. Empty until a zip resolves.
   */
  events: Event[];
  isLoading?: boolean;
  seriesInfo?: Map<string, SeriesInfo>;
  initialEvent?: Event;
  /** How many events could not be placed, and so are not in `events` at all. */
  unplaceableCount: number;
  // Location filter state, shared with the sidebar.
  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
  distance: number;
  onDistanceChange: (distance: number) => void;
  location: GeocodingResult | null;
  onLocationChange: (location: GeocodingResult | null) => void;
}

/**
 * Browse by distance.
 *
 * The other views start from a date and ask what is on. This one starts from
 * where you are: set a zip, pick how far you will travel, and get the dances
 * grouped by how soon they are with the closest first in each group. Series
 * are collapsed to one entry, as in the list — a weekly dance eight miles away
 * is one answer to "what is near me", not twenty.
 */
export function NearbyView({
  events,
  isLoading,
  seriesInfo,
  initialEvent,
  unplaceableCount,
  zipCode,
  onZipCodeChange,
  distance,
  onDistanceChange,
  location,
  onLocationChange,
}: NearbyViewProps) {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const initialEventOpened = useRef(false);

  /**
   * Whether this visitor has resolved a zip yet, in this view.
   *
   * Without it, clearing a digit to retype would drop `location` and collapse
   * the whole tab back to its opening prompt — unmounting the very field being
   * typed into, so the next keystroke lands nowhere. Once someone has searched,
   * the searching layout stays put and only the results below it change.
   */
  const [hasSearched, setHasSearched] = useState(false);
  useEffect(() => {
    if (location) setHasSearched(true);
  }, [location]);

  useEffect(() => {
    if (initialEvent && !initialEventOpened.current) {
      initialEventOpened.current = true;
      setSelectedEvent(initialEvent);
    }
  }, [initialEvent]);

  // Nothing to search from yet: the tab asks for a zip in the middle of the
  // page rather than sending the reader off to find the filter sidebar.
  if (!location && !hasSearched) {
    return (
      <div className="flex flex-col items-center text-center py-16 sm:py-24 px-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
          <Navigation className="w-7 h-7 text-muted-foreground" />
        </div>
        <h2 className="font-semibold text-xl text-foreground mb-2">
          Please enter your zip code to find dances happening near you
        </h2>
        <p className="text-muted-foreground max-w-sm mb-6">
          We'll show what's coming up, closest first.
        </p>
        <div className="w-full max-w-xs">
          <ZipField
            id="nearby-zipcode"
            size="large"
            autoFocus
            zipCode={zipCode}
            onZipCodeChange={onZipCodeChange}
            onLocationChange={onLocationChange}
            location={location}
          />
        </div>
      </div>
    );
  }

  // Without an origin nothing has been measured, so there is nothing to rank.
  // Guarding here rather than trusting `distanceMiles` to be present keeps a
  // half-typed zip from rendering the whole calendar as "0 mi away".
  const buckets = location
    ? bucketByTime(
        events.map((event) => ({ ...event, distanceMiles: event.distanceMiles ?? 0 })),
        getTodayISO()
      )
    : [];

  return (
    <>
      {/* Where we're searching from, and how far — both editable in place, and
          always on screen. Somebody travelling, or just curious about another
          town, retypes five digits here rather than going to find a filter
          panel. */}
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <ZipField
          id="nearby-zipcode"
          className="w-32"
          showConfirmation={false}
          zipCode={zipCode}
          onZipCodeChange={onZipCodeChange}
          onLocationChange={onLocationChange}
          location={location}
        />
        <DistanceSelect
          distance={distance}
          onDistanceChange={onDistanceChange}
          id="nearby-distance"
          className="w-40"
        />
      </div>

      {location ? (
        <p className="text-sm text-muted-foreground mb-5">
          {events.length} event{events.length !== 1 ? "s" : ""} near{" "}
          <span className="font-medium text-foreground">
            {location.label ?? zipCode}
          </span>
          {distance !== ANY_DISTANCE && <> within {distance} miles</>}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground mb-5">
          Enter a zip code to see what's coming up nearby.
        </p>
      )}

      {/* Events we know about but cannot place. Saying so is the difference
          between "nothing is on" and "we don't know where these are". */}
      {location && <UnplaceableNote count={unplaceableCount} className="mb-5" />}

      {!location ? null : isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-40 bg-card rounded-2xl border border-border animate-pulse"
            />
          ))}
        </div>
      ) : buckets.length === 0 ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
            <CalendarX className="w-7 h-7 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-xl text-foreground mb-2">
            Nothing within {distance === ANY_DISTANCE ? "range" : `${distance} miles`}
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            Try a wider radius, or clear your other filters.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {buckets.map((bucket) => (
            <section
              key={bucket.key}
              className="animate-in fade-in slide-in-from-bottom-3 duration-500"
            >
              <header className="mb-4 flex items-baseline gap-2">
                <h2 className="text-sm font-semibold tracking-wide text-muted-foreground">
                  {bucket.label.toUpperCase()}
                </h2>
                <span className="text-xs text-muted-foreground/70">
                  {bucket.events.length}
                </span>
              </header>
              <div className="space-y-4">
                {bucket.events.map((event) => (
                  <EventCard
                    key={event._id}
                    event={event}
                    onClick={() => setSelectedEvent(event)}
                    seriesInfo={seriesInfo?.get(event._id as string)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </>
  );
}
